/**
 * React Hook for Capacitor App Updates
 * 
 * Checks for updates on app startup and redirects to Play Store for installation
 * Since the app uses remote server loading, code updates are instant via deploys.
 * This hook handles native APK version updates via Play Store.
 * 
 * Features:
 * - Version checking against server
 * - Periodic background checks via portalSessionManager
 * - Persistent banner until user dismisses
 * - Play Store redirect for native app updates
 * 
 * @author TotalAccess Development Team
 * @date November 27, 2025
 */

'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import capacitorOTAUpdateService from '@/lib/capacitorOTAUpdateService';
import type { UpdateCheckResult, UpdateProgress } from '@/lib/capacitorOTAUpdateService';

import { portalSessionManager } from '@/lib/portalSessionManager';

export interface UseCapacitorOTAUpdateReturn {
  // State
  isChecking: boolean;
  updateAvailable: boolean;
  updateInfo: UpdateCheckResult | null;
  progress: UpdateProgress | null;
  showUpdateDialog: boolean;
  showUpdateBanner: boolean;  // Persistent banner for background checks
  currentVersion: string;
  
  // Actions
  checkForUpdates: (forceCheck?: boolean) => Promise<UpdateCheckResult | null>;
  downloadAndInstall: () => Promise<void>;  // Opens Play Store
  openPlayStore: () => Promise<void>;       // Alias for clarity
  skipVersion: () => void;
  dismissDialog: () => void;
  dismissBanner: () => void;  // Dismiss the persistent banner (won't show again for this version)
}

export function useCapacitorOTAUpdate(
  checkOnMount: boolean = true
): UseCapacitorOTAUpdateReturn {
  const [isChecking, setIsChecking] = useState(false);
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [updateInfo, setUpdateInfo] = useState<UpdateCheckResult | null>(null);
  const [progress, setProgress] = useState<UpdateProgress | null>(null);
  const [showUpdateDialog, setShowUpdateDialog] = useState(false);
  const [showUpdateBanner, setShowUpdateBanner] = useState(false);
  const callbackRegistered = useRef(false);

  /**
   * Check for updates
   * @param forceCheck - Bypass local cooldown (for manual checks)
   * @returns UpdateCheckResult or null if locally throttled
   */
  const checkForUpdates = useCallback(async (forceCheck: boolean = false): Promise<UpdateCheckResult | null> => {
    const throttle = capacitorOTAUpdateService.getUpdateCheckThrottleStatus(forceCheck);
    // 'not-native' still falls through — the service returns a stub result so
    // callers can render version details instead of silently doing nothing.
    if (!throttle.allowed && throttle.reason !== 'not-native') {
      const reason = throttle.reason === 'cooldown-active'
        ? `local cooldown active: ${throttle.remainingMinutes}m remaining`
        : throttle.reason;
      console.log(`[AppUpdate Hook] Skipping update check (${reason})`);
      return null;
    }

    setIsChecking(true);

    try {
      const result = await capacitorOTAUpdateService.checkForUpdates();
      
      setUpdateInfo(result);
      setUpdateAvailable(result.updateAvailable);

      // Show dialog if update available and not skipped
      if (result.updateAvailable) {
        const hasSkipped = capacitorOTAUpdateService.hasSkippedVersion(result.latestVersion);
        const isDismissed = portalSessionManager.isVersionDismissed(result.latestVersion);
        
        // Always show mandatory updates
        if (result.mandatory) {
          setShowUpdateDialog(true);
        } else if (!hasSkipped) {
          setShowUpdateDialog(true);
        }

        // Also show banner immediately if the version hasn't been dismissed
        // (previously banner only showed via 60s background check)
        if (!isDismissed) {
          setShowUpdateBanner(true);
        }
      }
      // Return result so callers can use it immediately
      return result;
    } catch (error) {
      console.error('[AppUpdate Hook] Update check error:', error);
      return null;
    } finally {
      setIsChecking(false);
    }
  }, []);

  /**
   * Open Play Store for update
   * Named downloadAndInstall for backward compatibility with existing UI
   */
  const openPlayStore = useCallback(async () => {
    try {
      const success = await capacitorOTAUpdateService.openPlayStore();

      if (success) {
        console.log('[AppUpdate Hook] Play Store opened successfully');
        // Clear skipped version since they're going to update
        capacitorOTAUpdateService.clearSkippedVersion();
        setShowUpdateDialog(false);
      }
    } catch (error) {
      console.error('[AppUpdate Hook] Failed to open Play Store:', error);
    }
  }, []);

  // Alias for backward compatibility
  const downloadAndInstall = openPlayStore;

  /**
   * Skip this version
   */
  const skipVersion = useCallback(() => {
    if (updateInfo?.latestVersion && !updateInfo.mandatory) {
      capacitorOTAUpdateService.setSkippedVersion(updateInfo.latestVersion);
      setShowUpdateDialog(false);
      console.log('[AppUpdate Hook] Version skipped:', updateInfo.latestVersion);
    }
  }, [updateInfo]);

  /**
   * Dismiss update dialog
   */
  const dismissDialog = useCallback(() => {
    if (!updateInfo?.mandatory) {
      setShowUpdateDialog(false);
    }
  }, [updateInfo]);

  /**
   * Dismiss update banner (persistent notification from background checks)
   * Won't show again for this version
   */
  const dismissBanner = useCallback(() => {
    if (updateInfo?.latestVersion && !updateInfo.mandatory) {
      portalSessionManager.dismissUpdate(updateInfo.latestVersion);
      setShowUpdateBanner(false);
      console.log('[AppUpdate Hook] Banner dismissed for version:', updateInfo.latestVersion);
    }
  }, [updateInfo]);

  /**
   * Subscribe to progress updates
   */
  useEffect(() => {
    const unsubscribe = capacitorOTAUpdateService.subscribe((newProgress) => {
      setProgress(newProgress);
    });

    return unsubscribe;
  }, []);

  /**
   * Register with portalSessionManager for background update checks
   * This allows the session manager to check for updates periodically
   * and notify us via callback
   */
  useEffect(() => {
    if (!capacitorOTAUpdateService.isNative() || callbackRegistered.current) return;

    callbackRegistered.current = true;

    const handleBackgroundUpdate = (updateNotification: {
      available: boolean;
      version: string;
      releaseNotes?: string;
      platform: 'capacitor' | 'installed-pwa';
      versionCheckResult?: import('@/lib/capacitorOTAUpdateService').UpdateCheckResult;
    }) => {
      if (updateNotification.platform !== 'capacitor') {
        return;
      }

      if (updateNotification.available) {
        console.log('[AppUpdate Hook] Background update notification:', updateNotification.version);
        
        // Use full version check result if available, otherwise construct partial
        const bg = updateNotification.versionCheckResult;
        const backgroundUpdateInfo: UpdateCheckResult = bg
          ? {
              ...bg,
              storeUrl: bg.storeUrl || capacitorOTAUpdateService.getStoreUrl(),
              playStoreUrl: bg.playStoreUrl || capacitorOTAUpdateService.getPlayStoreUrl(),
            }
          : {
              updateAvailable: true,
              currentVersion: capacitorOTAUpdateService.getCurrentVersion(),
              latestVersion: updateNotification.version,
              playStoreUrl: capacitorOTAUpdateService.getPlayStoreUrl(),
              releaseNotes: updateNotification.releaseNotes,
              mandatory: false,
            };
        
        setUpdateInfo(backgroundUpdateInfo);
        setUpdateAvailable(true);
        setShowUpdateBanner(true);  // Show banner for background updates
      }
    };

    portalSessionManager.registerUpdateCallback(handleBackgroundUpdate);

    return () => {
      portalSessionManager.unregisterUpdateCallback(handleBackgroundUpdate);
      callbackRegistered.current = false;
    };
  }, []);

  /**
   * Check for updates on mount
   */
  useEffect(() => {
    if (checkOnMount && capacitorOTAUpdateService.isNative()) {
      // Delay check slightly to not block app startup
      const timer = setTimeout(() => {
        checkForUpdates();
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [checkOnMount, checkForUpdates]);

  return {
    isChecking,
    updateAvailable,
    updateInfo,
    progress,
    showUpdateDialog,
    showUpdateBanner,
    currentVersion: capacitorOTAUpdateService.getCurrentVersion(),
    checkForUpdates,
    downloadAndInstall,
    openPlayStore,
    skipVersion,
    dismissDialog,
    dismissBanner,
  };
}

export default useCapacitorOTAUpdate;
